/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { InlineNotification } from '@coursera/cds-core';

import type { PreviewModeChangeFailure } from 'bundles/unified-common/contexts/org-scoped/OrgScopedImpersonationContext';

import _t from 'i18n!nls/unified-home-common';

const styles = {
  notification: css`
    margin: var(--cds-spacing-200) 0;
  `,
};

type Props = {
  /** The preview change that failed, which sets the title. */
  action: 'update' | 'exit';
  /** Why it failed, or null when there is no error to show. */
  failure: PreviewModeChangeFailure | null;
  onDismiss?: () => void;
};

/**
 * Error for a failed attempt to update or exit the admin preview. The alert region stays mounted so
 * screen readers announce the notification when it appears.
 */
const OrgScopedPreviewModeChangeError = ({ action, failure, onDismiss }: Props) => (
  <div role="alert">
    {failure && (
      <InlineNotification
        severity="error"
        title={action === 'exit' ? _t('We couldn’t exit your preview') : _t('We couldn’t update your preview')}
        onDismiss={onDismiss}
        css={styles.notification}
        data-testid="preview-mode-change-error"
      >
        {failure === 'permissionDenied'
          ? _t(
              'You may be missing the required permissions. Please confirm that your Power User role or admin role allows previewing the learner experience, then try again.'
            )
          : _t('Something went wrong. Please try again.')}
      </InlineNotification>
    )}
  </div>
);

export default OrgScopedPreviewModeChangeError;
